import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Loader } from '@piggytechng/component-library';
import { PiBaby, PiUsersThree } from 'react-icons/pi';
import { IoCopyOutline } from 'react-icons/io5';
import { httpClient } from '../../../../api/http';
import NoData from '../../../../components/NoData/NoData';
import Pagination from '../../../../components/Pagination/Pagination';
import TransactionDetailsAlt from '../../../../components/NewSideSheet/TransactionDetailsAlt/TransactionDetailsAlt';
import { copyToClipboard, formatToDollar, formatToNaira } from '../../../../helpers/utils';
import {
  ManagedAccount,
  ManagedAccountAction,
  ManagedAccountCharge,
  ManagedAccountCurrency,
  ManagedAccountHold,
  ManagedAccountInvite,
  ManagedAccountKind,
  ManagedAccountLedgerEntry,
  ManagedAccountLedgerPage,
  ManagedAccountMember,
  ManagedAccountOverview,
  ManagedAccountPlan,
  ManagedAccountRequest,
  ManagedAccountSection,
  ManagedAccountWallet,
  PagedResult,
} from '../../../../types';
import successIcon from '../../../../assets/icons/succesIcon.svg';
import failureIcon from '../../../../assets/icons/failureIcon.svg';
import '../../../../components/Table/Table.scss';
import './ManagedAccountsTab.scss';

// ---------------------------------------------------------------------------
//  The Kids and Joint accounts this customer is (or was) on, read through the customer. Everything here is read-only:
//  the owner acts in the app, and fraud holds live in the managed-account controls. Built from the same pieces as the
//  other customer tabs: tab-nav pills, the history pill switch, the shared table, NoData and Pagination.
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

const sections: { value: ManagedAccountSection; label: string }[] = [
  { value: 'transactions', label: 'Transactions' },
  { value: 'members', label: 'Members' },
  { value: 'invites', label: 'Invites' },
  { value: 'funding', label: 'Funding' },
  { value: 'requests', label: 'Requests' },
  { value: 'plans', label: 'Plans' },
  { value: 'activity', label: 'Activity' },
];

// Same order and labels as the Wallets History nav, minus the wallets a managed account cannot hold
const wallets: { value: ManagedAccountWallet; label: string }[] = [
  { value: 'flexnaira', label: 'Flex Naira' },
  { value: 'piggybank', label: 'PiggyBank' },
  { value: 'flexdollar', label: 'Flex Dollar' },
  { value: 'housemoney', label: 'HouseMoney' },
];

const KIND_LABEL: Record<ManagedAccountKind, string> = { kids: 'Kids', joint: 'Joint' };
const DESTINATION_LABEL: Record<string, string> = {
  piggyflex: 'Flex Naira',
  piggybank: 'PiggyBank',
  target: 'Target',
  getit: 'GetIT',
};
const PRODUCT_LABEL: Record<string, string> = { piggybank: 'PiggyBank', target: 'Target', getit: 'GetIT' };

const money = (amount: number, currency: ManagedAccountCurrency = 'NGN') =>
  currency === 'USD' ? formatToDollar(amount) : formatToNaira(amount);
const signedAmount = (entry: ManagedAccountLedgerEntry) => (entry.direction === 'debit' ? -entry.amount : entry.amount);
const readableDate = (value: string | null | undefined) => {
  if (!value) return '-----';
  const date = dayjs(value);
  return date.isValid() ? date.format('D MMM YYYY | h:mm A') : '-----';
};
const readableDay = (value: string | null | undefined) => {
  if (!value) return '-----';
  const date = dayjs(value);
  return date.isValid() ? date.format('D MMM YYYY') : '-----';
};
const capitalise = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);
// The product's own words for an action key, e.g. joint.request_approve → "Request approve"
const actionLabel = (action: string) => capitalise(action.split('.').pop()!.replace(/_/g, ' '));

const StatusChip = ({ value }: { value: string }) => (
  <span className={`managed-chip managed-chip--${value}`}>{capitalise(value)}</span>
);

const ManagedAccountsTab = ({ customerId }: { customerId: string }) => {
  const [selectedRef, setSelectedRef] = useState('');
  const [section, setSection] = useState<ManagedAccountSection>('transactions');
  const [wallet, setWallet] = useState<ManagedAccountWallet>('flexnaira');
  const [page, setPage] = useState(1);
  const [show, setShow] = useState(false);
  const [trDetails, setTrDetails] = useState({});
  const [detailsCurrency, setDetailsCurrency] = useState<ManagedAccountCurrency>('NGN');

  const base = `admin/user/${encodeURIComponent(customerId)}/managed-accounts`;
  const accountBase = `${base}/${encodeURIComponent(selectedRef)}`;
  const read = <T,>(url: string, params?: Record<string, string | number>) =>
    httpClient.get(url, params ? { params } : undefined).then((response) => response.data.data as T);

  const accountsQuery = useQuery<{ items: ManagedAccount[] }>({
    queryKey: ['customer-managed-accounts', customerId],
    enabled: !!customerId,
    retry: false,
    queryFn: () => read(base),
  });
  const accounts = accountsQuery.data?.items || [];

  useEffect(() => {
    if (accounts.length && !accounts.some((account) => account.ref === selectedRef)) setSelectedRef(accounts[0].ref);
  }, [accounts, selectedRef]);

  useEffect(() => {
    setSelectedRef('');
    setSection('transactions');
    setWallet('flexnaira');
    setPage(1);
  }, [customerId]);

  const selected = accounts.find((account) => account.ref === selectedRef);
  const ready = !!customerId && !!selectedRef;
  const currency: ManagedAccountCurrency = wallet === 'flexdollar' ? 'USD' : 'NGN';

  const overviewQuery = useQuery<ManagedAccountOverview>({
    queryKey: ['customer-managed-overview', customerId, selectedRef],
    enabled: ready,
    retry: false,
    queryFn: () => read(accountBase),
  });
  const ledgerQuery = useQuery<ManagedAccountLedgerPage>({
    queryKey: ['customer-managed-ledger', customerId, selectedRef, wallet, page],
    enabled: ready && section === 'transactions',
    retry: false,
    queryFn: () => read(`${accountBase}/transactions`, { wallet, page, limit: PAGE_SIZE }),
  });
  const invitesQuery = useQuery<{ items: ManagedAccountInvite[] }>({
    queryKey: ['customer-managed-invites', customerId, selectedRef],
    enabled: ready && section === 'invites',
    retry: false,
    queryFn: () => read(`${accountBase}/invites`),
  });
  const fundingQuery = useQuery<PagedResult<ManagedAccountCharge>>({
    queryKey: ['customer-managed-funding', customerId, selectedRef, page],
    enabled: ready && section === 'funding',
    retry: false,
    queryFn: () => read(`${accountBase}/funding`, { page, limit: PAGE_SIZE }),
  });
  const requestsQuery = useQuery<{ items: ManagedAccountRequest[] }>({
    queryKey: ['customer-managed-requests', customerId, selectedRef],
    enabled: ready && section === 'requests',
    retry: false,
    queryFn: () => read(`${accountBase}/requests`),
  });
  const plansQuery = useQuery<{ items: ManagedAccountPlan[] }>({
    queryKey: ['customer-managed-plans', customerId, selectedRef],
    enabled: ready && section === 'plans',
    retry: false,
    queryFn: () => read(`${accountBase}/plans`),
  });
  const activityQuery = useQuery<PagedResult<ManagedAccountAction>>({
    queryKey: ['customer-managed-activity', customerId, selectedRef, page],
    enabled: ready && section === 'activity',
    retry: false,
    queryFn: () => read(`${accountBase}/activity`, { page, limit: PAGE_SIZE }),
  });

  const selectAccount = (ref: string) => {
    setSelectedRef(ref);
    setPage(1);
  };
  const selectSection = (next: ManagedAccountSection) => {
    setSection(next);
    setPage(1);
  };
  const selectWallet = (next: ManagedAccountWallet) => {
    setWallet(next);
    setPage(1);
  };

  // TransactionDetailsAlt renders whatever keys it is given, so each row is shaped into the labels support expects
  const openDetails = (payload: Record<string, unknown>, payloadCurrency: ManagedAccountCurrency = 'NGN') => {
    setDetailsCurrency(payloadCurrency);
    setTrDetails(payload);
    setShow(true);
  };

  if (accountsQuery.isLoading) {
    return (
      <div className="page-loader">
        <Loader color="#0F60D6" />
      </div>
    );
  }
  if (accountsQuery.isError) {
    return (
      <NoData
        message="Could not load managed accounts. Please try again."
        actionText="Retry"
        action={() => accountsQuery.refetch()}
      />
    );
  }
  if (!accounts.length) {
    return <NoData message="This customer is not on any Kids or Joint account." />;
  }

  const overview = overviewQuery.data;
  const totalPagesOf = (paged?: { total: number; limit: number }) =>
    paged ? Math.max(1, Math.ceil(paged.total / paged.limit)) : 1;

  const renderTable = <T,>(
    query: { isLoading: boolean; isError: boolean; refetch: () => unknown },
    rows: T[] | undefined,
    empty: string,
    head: React.ReactNode,
    body: (row: T, index: number) => React.ReactNode,
    modifier: string,
    paged?: { page: number; total: number; limit: number },
  ) => {
    if (query.isLoading) {
      return (
        <div className="page-loader">
          <Loader color="#0F60D6" />
        </div>
      );
    }
    if (query.isError)
      return (
        <NoData
          message="Could not load this view. Please try again."
          actionText="Retry"
          action={() => query.refetch()}
        />
      );
    if (!rows?.length) return <NoData message={empty} />;
    return (
      <>
        <div className={`table-container managed-table managed-table--${modifier}`}>
          <table className="table">
            <thead className="table_head">
              <tr>{head}</tr>
            </thead>
            <tbody className="table-body">{rows.map(body)}</tbody>
          </table>
        </div>
        {paged && <Pagination currentPage={paged.page} totalPages={totalPagesOf(paged)} onPageChange={setPage} />}
      </>
    );
  };

  return (
    <div className="managed-accounts-tab">
      <div className="customer-page-tab-nav flex--2 pointer" aria-label="Select managed account">
        {accounts.map((account) => {
          const isActive = account.ref === selectedRef;
          const tint = account.kind === 'kids' ? { bg: '#edf1fc', fg: '#1734ca' } : { bg: '#f3ebff', fg: '#6624ce' };
          return (
            <div
              key={account.ref}
              role="button"
              aria-pressed={isActive}
              className={`link flex--2 ${isActive ? 'active' : ''}`}
              style={{
                backgroundColor: isActive ? tint.bg : 'transparent',
                color: isActive ? tint.fg : '#4B5563',
                borderColor: isActive ? 'transparent' : '#B8B8B8',
              }}
              onClick={() => selectAccount(account.ref)}
            >
              <span className="icon flex--2" style={{ color: isActive ? tint.fg : '#5F5F5F' }}>
                {account.kind === 'kids' ? <PiBaby size={20} /> : <PiUsersThree size={20} />}
              </span>
              <span className="label">{account.name}</span>
              <small className="managed-accounts-tab__kind">{KIND_LABEL[account.kind]}</small>
            </div>
          );
        })}
      </div>

      {selected && (
        <div className="wallets-details">
          <div className="managed-accounts-tab__summary">
            <div className="managed-accounts-tab__summary-title">
              <h1 className="heading">{selected.name}</h1>
              <span className={`customer-status-badge customer-status-badge--${selected.status}`}>
                <span className="customer-status-badge__dot" />
                {selected.status}
              </span>
              {selected.membership.status === 'removed' && (
                <span
                  className="customer-status-badge customer-status-badge--removed"
                  title={`Left ${readableDay(selected.membership.removedAt)}`}
                >
                  <span className="customer-status-badge__dot" />
                  No longer a member
                </span>
              )}
              {(overview?.risk.held ?? selected.held) && (
                <span
                  className="customer-status-badge customer-status-badge--flagged"
                  title="Money cannot leave this account until the hold is cleared"
                >
                  <span className="customer-status-badge__dot" />
                  Money out on hold
                </span>
              )}
            </div>
            <div className="managed-accounts-tab__summary-meta">
              <div className="managed-accounts-tab__chip">
                Account ID: {selected.ref}
                <IoCopyOutline
                  className="pointer"
                  size={16}
                  onClick={() => copyToClipboard('Account ID', selected.ref)}
                />
              </div>
              <div className="managed-accounts-tab__chip">Customer role: {selected.roleLabel}</div>
              {selected.access.label && (
                <div className="managed-accounts-tab__chip">Customer access: {selected.access.label}</div>
              )}
              <div className="managed-accounts-tab__chip">
                Members:{' '}
                {overview ? overview.members.filter((m) => m.status === 'active').length : selected.memberCount}
              </div>
              {overview && (
                <div className="managed-accounts-tab__chip">
                  Flex Naira {money(overview.balances.flexNaira)} · PiggyBank {money(overview.balances.piggybank)}
                </div>
              )}
              <div className="managed-accounts-tab__chip">Read only</div>
            </div>
          </div>

          <div className="history-tab-switch">
            {sections.map((option) => (
              <span
                key={option.value}
                role="button"
                aria-pressed={section === option.value}
                className={`history-pill${section === option.value ? ' active' : ''}`}
                onClick={() => selectSection(option.value)}
              >
                {option.label}
              </span>
            ))}
          </div>

          {section === 'transactions' && (
            <div className={`history-tab-switch managed-accounts-tab__wallets ${wallet}`}>
              {wallets.map((option) => (
                <span
                  key={option.value}
                  role="button"
                  aria-pressed={wallet === option.value}
                  className={`history-pill${wallet === option.value ? ' active' : ''}`}
                  onClick={() => selectWallet(option.value)}
                >
                  {option.label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {selected && section === 'transactions' && (
        <>
          {renderTable<ManagedAccountLedgerEntry>(
            ledgerQuery,
            ledgerQuery.data?.items,
            `No ${wallets.find((w) => w.value === wallet)?.label} transactions at the moment.`,
            <>
              <th className="icon">{''}</th>
              <th className="date">Date</th>
              <th className="amount">{`Amount (${currency === 'USD' ? '$' : '₦'})`}</th>
              <th className="balance">{`Balance (${currency === 'USD' ? '$' : '₦'})`}</th>
              <th className="memo">Memo</th>
              <th className="reference">Reference</th>
            </>,
            (entry, index) => (
              <tr
                key={`${entry.reference}-${index}`}
                className="table-row"
                onClick={() =>
                  openDetails(
                    {
                      amount: signedAmount(entry),
                      created_at: entry.createdAt ?? '',
                      memo: entry.description,
                      reference: entry.reference,
                      balance: entry.balanceAfter == null ? '-----' : money(entry.balanceAfter, currency),
                      wallet: wallets.find((w) => w.value === wallet)?.label,
                      account: `${selected.name} (${selected.ref})`,
                      status: 'Posted',
                    },
                    currency,
                  )
                }
              >
                <td className="icon">
                  <img src={entry.direction === 'credit' ? successIcon : failureIcon} alt={entry.direction} />
                </td>
                <td className="date">{readableDate(entry.createdAt)}</td>
                <td className="amount">{money(signedAmount(entry), currency)}</td>
                <td className="balance">
                  {entry.balanceAfter == null ? '-----' : money(entry.balanceAfter, currency)}
                </td>
                <td className="memo" title={entry.description}>
                  {entry.description || '-----'}
                </td>
                <td className="reference">{entry.reference || '-----'}</td>
              </tr>
            ),
            'ledger',
            ledgerQuery.data,
          )}
        </>
      )}

      {selected && section === 'members' && (
        <>
          {renderTable<ManagedAccountMember>(
            overviewQuery,
            overview?.members,
            'No members found on this account.',
            <>
              <th className="name">Name</th>
              <th className="role">Role</th>
              <th className="access">Access</th>
              <th className="relationship">Relationship</th>
              <th className="status">Status</th>
              <th className="date">Joined</th>
              <th className="date">Left</th>
            </>,
            (member) => (
              <tr
                key={member.userId}
                className="table-row"
                onClick={() => window.open(`/customer/${member.userId}`, '_blank', 'noopener')}
              >
                <td className="name" title={member.email || member.userId}>
                  {member.name}
                </td>
                <td className="role">{member.roleLabel}</td>
                <td className="access">{member.access.label || 'Full access'}</td>
                <td className="relationship">{member.relationshipLabel || '-----'}</td>
                <td className="status">
                  <StatusChip value={member.status} />
                  {member.blacklisted && <StatusChip value="blacklisted" />}
                  {member.blocked && <StatusChip value="blocked" />}
                </td>
                <td className="date">{readableDay(member.joinedAt)}</td>
                <td className="date">{readableDay(member.removedAt)}</td>
              </tr>
            ),
            'members',
          )}
          {!!overview?.risk.holds?.length && (
            <div className="managed-accounts-tab__holds">
              <h3>Holds</h3>
              <p>
                Money cannot leave the account while a hold is open. Holds are placed and cleared in the managed-account
                controls.
              </p>
              {renderTable<ManagedAccountHold>(
                overviewQuery,
                overview.risk.holds,
                '',
                <>
                  <th className="source">Source</th>
                  <th className="memo">Reason</th>
                  <th className="name">Placed by</th>
                  <th className="date">Placed</th>
                  <th className="name">Cleared by</th>
                  <th className="date">Cleared</th>
                </>,
                (hold) => (
                  <tr key={hold.id} className="table-row managed-table__static">
                    <td className="source">
                      {hold.source === 'ops'
                        ? 'Ops'
                        : hold.source === 'block'
                          ? `Blocked: ${hold.triggerName || ''}`
                          : `Blacklist: ${hold.triggerName || ''}`}
                    </td>
                    <td className="memo" title={hold.reason || ''}>
                      {hold.reason || '-----'}
                    </td>
                    <td className="name">{hold.createdByName || '-----'}</td>
                    <td className="date">{readableDate(hold.createdAt)}</td>
                    <td className="name">{hold.clearedByName || '-----'}</td>
                    <td className="date">
                      {hold.clearedAt ? readableDate(hold.clearedAt) : <StatusChip value="open" />}
                    </td>
                  </tr>
                ),
                'holds',
              )}
            </div>
          )}
        </>
      )}

      {selected &&
        section === 'invites' &&
        renderTable<ManagedAccountInvite>(
          invitesQuery,
          invitesQuery.data?.items,
          'No invites have been sent from this account.',
          <>
            <th className="contact">Invited</th>
            <th className="role">Role</th>
            <th className="access">Access</th>
            <th className="status">Status</th>
            <th className="count">Sent</th>
            <th className="name">Invited by</th>
            <th className="date">Expires</th>
          </>,
          (invite) => (
            <tr key={invite.id} className="table-row managed-table__static">
              <td className="contact" title={invite.invitedUser?.name || ''}>
                {invite.invitedUser ? `${invite.invitedUser.name} · ` : ''}
                {invite.contact || '-----'}
              </td>
              <td className="role">{invite.roleLabel}</td>
              <td className="access">{invite.access.label || 'Full access'}</td>
              <td className="status">
                <StatusChip value={invite.status} />
              </td>
              <td className="count">{invite.sendCount}×</td>
              <td className="name">{invite.invitedBy.name}</td>
              <td className="date">{readableDate(invite.expiresAt)}</td>
            </tr>
          ),
          'invites',
        )}

      {selected &&
        section === 'funding' &&
        renderTable<ManagedAccountCharge>(
          fundingQuery,
          fundingQuery.data?.items,
          'No bank or card funding on this account yet.',
          <>
            <th className="icon">{''}</th>
            <th className="date">Date</th>
            <th className="name">Paid by</th>
            <th className="method">Method</th>
            <th className="amount">Amount (₦)</th>
            <th className="destination">Into</th>
            <th className="status">Status</th>
            <th className="reference">Reference</th>
          </>,
          (charge) => (
            <tr
              key={charge.reference}
              className="table-row"
              onClick={() =>
                openDetails({
                  amount: charge.amount,
                  created_at: charge.createdAt,
                  paid_by: charge.payer.name,
                  method: charge.method,
                  destination: DESTINATION_LABEL[charge.destination] || charge.destination,
                  purpose: charge.purpose || '-----',
                  status: capitalise(charge.status),
                  failure_reason: charge.failureReason || '-----',
                  settled_at: charge.settledAt ? readableDate(charge.settledAt) : '-----',
                  reference: charge.reference,
                })
              }
            >
              <td className="icon">
                <img src={charge.status === 'failed' ? failureIcon : successIcon} alt={charge.status} />
              </td>
              <td className="date">{readableDate(charge.createdAt)}</td>
              <td className="name">{charge.payer.name}</td>
              <td className="method">{capitalise(charge.method)}</td>
              <td className="amount">{money(charge.amount)}</td>
              <td className="destination">{DESTINATION_LABEL[charge.destination] || charge.destination}</td>
              <td className="status">
                <StatusChip value={charge.status} />
              </td>
              <td className="reference">{charge.reference}</td>
            </tr>
          ),
          'funding',
          fundingQuery.data,
        )}

      {selected &&
        section === 'requests' &&
        renderTable<ManagedAccountRequest>(
          requestsQuery,
          requestsQuery.data?.items,
          'No one has asked to move money out of this account.',
          <>
            <th className="date">Date</th>
            <th className="name">Requested by</th>
            <th className="amount">Amount</th>
            <th className="destination">Pay to</th>
            <th className="status">Status</th>
            <th className="name">Decided by</th>
            <th className="reference">Reference</th>
          </>,
          (request) => (
            <tr
              key={request.id}
              className="table-row"
              onClick={() =>
                openDetails({
                  amount: request.amount,
                  created_at: request.createdAt,
                  requested_by: request.requester.name,
                  pay_to: request.payoutDestination || 'Flex Naira',
                  note: request.note || '-----',
                  status: capitalise(request.status),
                  decided_by: request.decidedBy?.name || '-----',
                  decision_note: request.decisionNote || '-----',
                  decided_at: request.decidedAt ? readableDate(request.decidedAt) : '-----',
                  expires_at: readableDate(request.expiresAt),
                  reference: request.reference || '-----',
                })
              }
            >
              <td className="date">{readableDate(request.createdAt)}</td>
              <td className="name">{request.requester.name}</td>
              <td className="amount">{money(request.amount)}</td>
              <td className="destination">{capitalise(request.payoutDestination || 'flex')}</td>
              <td className="status">
                <StatusChip value={request.status} />
              </td>
              <td className="name">{request.decidedBy?.name || '-----'}</td>
              <td className="reference">{request.reference || '-----'}</td>
            </tr>
          ),
          'requests',
        )}

      {selected &&
        section === 'plans' &&
        renderTable<ManagedAccountPlan>(
          plansQuery,
          plansQuery.data?.items,
          'No recurring funding on this account.',
          <>
            <th className="product">Product</th>
            <th className="name">Paid by</th>
            <th className="amount">Amount (₦)</th>
            <th className="frequency">Frequency</th>
            <th className="status">Status</th>
            <th className="date">Next run</th>
            <th className="date">Last run</th>
            <th className="memo">Last result</th>
          </>,
          (plan) => (
            <tr key={plan.id} className="table-row managed-table__static">
              <td className="product">{PRODUCT_LABEL[plan.product] || plan.product}</td>
              <td className="name">{plan.payer.name}</td>
              <td className="amount">{plan.amount == null ? '-----' : money(plan.amount)}</td>
              <td className="frequency">{plan.frequency ? capitalise(plan.frequency) : '-----'}</td>
              <td className="status">
                <StatusChip value={plan.status} />
              </td>
              <td className="date">{readableDate(plan.nextRunAt)}</td>
              <td className="date">{readableDate(plan.lastRunAt)}</td>
              <td className="memo" title={plan.lastError || plan.lastResult || ''}>
                {plan.failureCount ? `${plan.failureCount} failed · ` : ''}
                {plan.lastError || plan.lastResult || '-----'}
              </td>
            </tr>
          ),
          'plans',
        )}

      {selected &&
        section === 'activity' &&
        renderTable<ManagedAccountAction>(
          activityQuery,
          activityQuery.data?.items,
          'Nothing has happened on this account yet.',
          <>
            <th className="date">Date</th>
            <th className="action">Action</th>
            <th className="name">By</th>
            <th className="amount">Amount</th>
            <th className="reference">Reference</th>
          </>,
          (entry) => (
            <tr
              key={entry.id}
              className="table-row"
              onClick={() =>
                openDetails(
                  {
                    ...(entry.amount != null ? { amount: entry.amount } : {}),
                    created_at: entry.createdAt,
                    action: actionLabel(entry.action),
                    by: entry.actor.name,
                    wallet: entry.wallet || '-----',
                    reference: entry.reference || '-----',
                    ...(entry.meta || {}),
                  },
                  entry.currency === 'USD' ? 'USD' : 'NGN',
                )
              }
            >
              <td className="date">{readableDate(entry.createdAt)}</td>
              <td className="action">{actionLabel(entry.action)}</td>
              <td className="name">{entry.actor.name}</td>
              <td className="amount">
                {entry.amount == null ? '-----' : money(entry.amount, entry.currency === 'USD' ? 'USD' : 'NGN')}
              </td>
              <td className="reference">{entry.reference || '-----'}</td>
            </tr>
          ),
          'activity',
          activityQuery.data,
        )}

      <TransactionDetailsAlt
        show={show}
        id={''}
        data={trDetails}
        isDollar={detailsCurrency === 'USD'}
        onClose={() => {
          setShow(false);
        }}
      />
    </div>
  );
};

export default ManagedAccountsTab;
